import { Trans, useLingui } from "@lingui/react/macro"
import type { VoiceCallDeal } from "@/types/v2/voice"
import { SparkleIcon, TrashIcon, CheckIcon, PlusIcon } from "./icons"
import { AfterCallSubviewHeader } from "./after-call-subview-header"

export type NoteVisibility = "team" | "personal"

interface MoreSheetProps {
  deal: VoiceCallDeal | null
  // Whether the linked deal is a rep-confirmed pick vs the AI's
  // still-a-guess derived deal. Gates the stage-advance opt-in
  // and drives the "AI picked this" confirm affordance (CON-170).
  dealConfirmedByRep: boolean
  // Opens the Link-a-deal picker for a call with NO deal. The
  // no-deal row routes here so linking is reachable from More
  // too — not just the card.
  onLinkDeal: () => void
  // Opens the SAME picker to change / clear an already-linked
  // deal (the picker pre-selects the current one and offers a
  // "Don't link a deal" clear row).
  onChangeDeal: () => void
  // Promotes an AI-derived deal to a rep-confirmed pick without
  // changing it — unlocks the stage-advance opt-in.
  onConfirmDeal: () => void
  visibility: NoteVisibility
  onVisibilityChange: (value: NoteVisibility) => void
  advanceStage: boolean
  onToggleAdvanceStage: () => void
  onRegenerate: () => void
  // Hidden when there's no note to delete yet (insights still
  // pending and the rep hasn't written one).
  canDeleteNote: boolean
  onDeleteNote: () => void
  onClose: () => void
}

// The post-call card's "More" sheet (CON-158, frame 5): the
// rare fields that don't belong inline — linked-deal context,
// the stage-advance opt-in, note visibility, regenerate, and
// delete. Replaces the old forms-first Edit screen.
//
// The linked-deal row is a live "Change deal" affordance
// (CON-170): tap it to reopen the picker and swap or clear the
// deal. An AI-derived deal is flagged as a guess with a "Keep
// this deal" confirm — and the stage-advance opt-in only shows
// once the rep has confirmed the deal, so we never nudge the
// stage of a deal the AI mis-derived.
export function AfterCallMoreSheet({
  deal,
  dealConfirmedByRep,
  onLinkDeal,
  onChangeDeal,
  onConfirmDeal,
  visibility,
  onVisibilityChange,
  advanceStage,
  onToggleAdvanceStage,
  onRegenerate,
  canDeleteNote,
  onDeleteNote,
  onClose,
}: MoreSheetProps) {
  const { t } = useLingui()
  const dealIsAiDerived = deal != null && !dealConfirmedByRep
  return (
    <div
      data-testid="after-call-more"
      className="flex flex-col gap-3.5 p-[18px] text-[#FBF7F0]"
    >
      <AfterCallSubviewHeader title={t`More`} onBack={onClose} />

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold text-[#9A938A]">
          <Trans>Linked deal</Trans>
        </span>
        {deal ? (
          <div className="flex flex-col gap-2 rounded-[9px] border border-[#3A352E] px-[11px] py-[9px]">
            <div className="flex items-center gap-2">
              <span className="min-w-0 grow truncate text-[13px] text-[#FBF7F0]">
                {dealLabel(deal)}
              </span>
              <button
                type="button"
                onClick={onChangeDeal}
                data-testid="after-call-change-deal"
                className="shrink-0 text-[11px] font-medium text-[#9A938A] underline"
              >
                <Trans>Change</Trans>
              </button>
            </div>
            {dealIsAiDerived ? (
              <div className="flex flex-col gap-2 border-t border-[#3A352E] pt-2">
                <div className="flex items-center gap-1.5">
                  <SparkleIcon
                    width={11}
                    height={11}
                    className="shrink-0 text-[#E0A56A]"
                  />
                  <span className="text-[11px] leading-[15px] text-[#9A938A]">
                    <Trans>AI picked this deal — confirm it's the right one.</Trans>
                  </span>
                </div>
                <button
                  type="button"
                  onClick={onConfirmDeal}
                  data-testid="after-call-confirm-deal"
                  className="self-start rounded-[8px] bg-[#3A2F1E] px-2.5 py-1 text-[11px] font-semibold text-[#E0A56A]"
                >
                  <Trans>Keep this deal</Trans>
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <button
            type="button"
            onClick={onLinkDeal}
            data-testid="after-call-more-link-deal"
            className="flex items-center gap-2 rounded-[9px] border border-[#3A352E] px-[11px] py-[9px] text-left hover:bg-[#2A2620]"
          >
            <PlusIcon width={13} height={13} className="shrink-0 text-[#E0A56A]" />
            <span className="grow text-[13px] font-medium text-[#E0A56A]">
              <Trans>Link a deal</Trans>
            </span>
          </button>
        )}
      </div>

      {/* Stage advance is only offered once the rep has confirmed
          the deal — an AI-derived guess can't be nudged (CON-170). */}
      {deal && dealConfirmedByRep ? (
        <button
          type="button"
          onClick={onToggleAdvanceStage}
          aria-pressed={advanceStage}
          data-testid="after-call-advance-stage"
          className="flex items-center gap-2.5 text-left"
        >
          <Box checked={advanceStage} />
          <span className="flex min-w-0 grow flex-col gap-px">
            <span className="text-[13px] text-[#FBF7F0]">
              <Trans>Move deal to the next stage</Trans>
            </span>
            <span className="text-xs text-[#9A938A]">
              <Trans>AI thinks this call advanced the stage</Trans>
            </span>
          </span>
        </button>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold text-[#9A938A]">
          <Trans>Note visibility</Trans>
        </span>
        <div className="flex gap-1.5">
          <VisibilityOption
            label={t`Team`}
            active={visibility === "team"}
            onClick={() => onVisibilityChange("team")}
          />
          <VisibilityOption
            label={t`Private`}
            active={visibility === "personal"}
            onClick={() => onVisibilityChange("personal")}
          />
        </div>
      </div>

      <div className="h-px bg-[#2A2620]" />

      <button
        type="button"
        onClick={onRegenerate}
        data-testid="after-call-regenerate"
        className="flex items-center gap-2.5 text-left text-[13px] text-[#E0A56A] hover:opacity-80"
      >
        <SparkleIcon width={14} height={14} className="shrink-0" />
        <Trans>Regenerate AI summary</Trans>
      </button>

      {canDeleteNote ? (
        <button
          type="button"
          onClick={onDeleteNote}
          data-testid="after-call-delete-note"
          className="flex items-center gap-2.5 text-left text-[13px] text-[#E5736F] hover:opacity-80"
        >
          <TrashIcon width={14} height={14} className="shrink-0" />
          <Trans>Delete this note</Trans>
        </button>
      ) : null}
    </div>
  )
}

function dealLabel(deal: VoiceCallDeal): string {
  return deal.stage ? `${deal.name} · ${deal.stage}` : deal.name
}

// The checkbox used for the stage-advance opt-in: empty
// hairline square that fills amber with a charcoal check.
function Box({ checked }: { checked: boolean }) {
  if (checked) {
    return (
      <span className="flex size-4 shrink-0 items-center justify-center rounded-[5px] bg-[#E0A56A]">
        <CheckIcon width={10} height={10} className="text-[#1E1B17]" />
      </span>
    )
  }
  return (
    <span className="size-4 shrink-0 rounded-[5px] border-[1.5px] border-[#6E685F]" />
  )
}

function VisibilityOption({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`grow rounded-lg p-2 text-center text-xs ${
        active
          ? "bg-[#F4EFE6] font-semibold text-[#1E1B17]"
          : "border border-[#3A352E] text-[#D9D3C8] hover:bg-[#2A2620]"
      }`}
    >
      {label}
    </button>
  )
}
